import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const WhatsNewModal = ({ open, release, onDismiss }) => {
  if (!release) return null;

  const hasFeatures = release.features?.length > 0;
  const hasFixes    = release.bug_fixes?.length > 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #E27534 0%, #c45e1a 100%)",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 22 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.3px" }}>
            What&rsquo;s New
          </Typography>
          {/* Version pill */}
          <Box
            sx={{
              ml: 0.5,
              px: 1.25,
              py: 0.25,
              borderRadius: "20px",
              background: "rgba(255,255,255,0.22)",
              border: "1px solid rgba(255,255,255,0.35)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.4px",
              lineHeight: "20px",
            }}
          >
            v{release.version}
          </Box>
        </Box>

        <Typography sx={{ fontSize: 15, fontWeight: 600, opacity: 0.95, mb: 0.25 }}>
          {release.title}
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
          Released {formatDate(release.release_date)}
        </Typography>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1, maxHeight: "55vh", overflowY: "auto" }}>

        {hasFeatures && (
          <Box sx={{ mb: hasFixes ? 2.5 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
              <AutoAwesomeIcon sx={{ fontSize: 17, color: "#E27534" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#E27534", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                New Features
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {release.features.map((item, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#22c55e", flexShrink: 0, mt: "2px" }} />
                  <Typography sx={{ fontSize: 13.5, color: "#374151", lineHeight: 1.55 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {hasFeatures && hasFixes && (
          <Divider sx={{ mb: 2.5 }} />
        )}

        {hasFixes && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
              <BugReportOutlinedIcon sx={{ fontSize: 17, color: "#6366f1" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                Bug Fixes &amp; Improvements
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {release.bug_fixes.map((item, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a5b4fc", flexShrink: 0, mt: "6px" }} />
                  <Typography sx={{ fontSize: 13.5, color: "#374151", lineHeight: 1.55 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          borderTop: "1px solid #f3f4f6",
          background: "#fafafa",
        }}
      >
        <Button
          variant="contained"
          onClick={onDismiss}
          sx={{
            background: "linear-gradient(135deg, #E27534 0%, #c45e1a 100%)",
            color: "#fff",
            borderRadius: "8px",
            px: 3,
            py: 0.9,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(226,117,52,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #c45e1a 0%, #a84e14 100%)",
              boxShadow: "0 4px 12px rgba(226,117,52,0.45)",
            },
          }}
        >
          Got it, thanks! 👍
        </Button>
      </Box>
    </Dialog>
  );
};

export default WhatsNewModal;
